<script>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DeliveryDriversAPI from 'dashboard/api/deliveryDrivers';
import PaymentClosingModal from './PaymentClosingModal.vue';

export default {
  name: 'QuoteList',
  components: {
    NextButton,
    PaymentClosingModal,
  },
  props: {
    quotes: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    'edit',
    'view',
    'delete',
    'update-status',
    'print',
    'separate',
    'out-for-delivery',
    'resend-notification',
    'mark-as-sold',
    'update-payments',
    'reopen-sale',
    'send-voucher',
  ],
  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const isAdmin = computed(
      () => store.getters.getCurrentUser?.role === 'administrator'
    );

    // Modal state
    const showSeparationModal = ref(false);
    const showDeliveryPersonModal = ref(false);
    const showPaymentModal = ref(false);
    const selectedQuoteForSeparation = ref(null);
    const selectedQuoteForPayment = ref(null);
    const deliveryPeople = ref([]);
    const loadingDeliveryPeople = ref(false);

    const openSeparationModal = quote => {
      selectedQuoteForSeparation.value = quote;
      showSeparationModal.value = true;
    };

    const closeSeparationModal = () => {
      showSeparationModal.value = false;
      selectedQuoteForSeparation.value = null;
    };

    const selectDeliveryType = async type => {
      if (type === 'pickup') {
        emit('separate', selectedQuoteForSeparation.value, {
          deliveryType: 'pickup',
          deliveryPersonName: null,
          deliveryPersonPhone: null,
        });
        closeSeparationModal();
      } else {
        // Verificar se tem taxa de entrega
        const quote = selectedQuoteForSeparation.value;
        if (!quote.delivery_fee || quote.delivery_fee <= 0) {
          alert(t('QUOTES.VALIDATION.NO_DELIVERY_FEE'));
          closeSeparationModal();
          emit('edit', quote);
          return;
        }

        // Para entrega, apenas marca como separado (sem entregador ainda)
        emit('separate', selectedQuoteForSeparation.value, {
          deliveryType: 'delivery',
          deliveryPersonName: null,
          deliveryPersonPhone: null,
        });
        closeSeparationModal();
      }
    };

    const loadDeliveryPeople = async () => {
      loadingDeliveryPeople.value = true;
      try {
        const response = await DeliveryDriversAPI.get({ active: true });
        deliveryPeople.value = response.data || [];
      } catch (error) {
        console.error('Error loading delivery people:', error);
        deliveryPeople.value = [];
      } finally {
        loadingDeliveryPeople.value = false;
      }
    };

    // Out for delivery modal (driver selection)
    const showOutForDeliveryModal = ref(false);
    const selectedQuoteForDelivery = ref(null);

    const openOutForDeliveryModal = async quote => {
      selectedQuoteForDelivery.value = quote;
      await loadDeliveryPeople();
      showOutForDeliveryModal.value = true;
    };

    const selectDeliveryPerson = person => {
      emit('out-for-delivery', selectedQuoteForDelivery.value, {
        deliveryPersonId: person.id,
        deliveryPersonName: person.name,
        deliveryPersonPhone: person.phone_number,
      });
      closeOutForDeliveryModal();
    };

    const closeOutForDeliveryModal = () => {
      showOutForDeliveryModal.value = false;
      selectedQuoteForDelivery.value = null;
    };

    // Payment modal functions
    const isEditingPayment = ref(false);

    const openPaymentModal = (quote, editMode = false) => {
      selectedQuoteForPayment.value = quote;
      isEditingPayment.value = editMode;
      showPaymentModal.value = true;
    };

    const closePaymentModal = () => {
      showPaymentModal.value = false;
      selectedQuoteForPayment.value = null;
      isEditingPayment.value = false;
    };

    const confirmPayment = payments => {
      if (isEditingPayment.value) {
        emit('update-payments', selectedQuoteForPayment.value, payments);
      } else {
        emit('mark-as-sold', selectedQuoteForPayment.value, payments);
      }
      closePaymentModal();
    };

    return {
      t,
      isAdmin,
      showSeparationModal,
      showOutForDeliveryModal,
      showPaymentModal,
      isEditingPayment,
      selectedQuoteForSeparation,
      selectedQuoteForDelivery,
      selectedQuoteForPayment,
      deliveryPeople,
      loadingDeliveryPeople,
      openSeparationModal,
      closeSeparationModal,
      selectDeliveryType,
      openOutForDeliveryModal,
      selectDeliveryPerson,
      closeOutForDeliveryModal,
      openPaymentModal,
      closePaymentModal,
      confirmPayment,
    };
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatCurrency(value) {
      return `R$ ${Number(value || 0).toFixed(2)}`;
    },
    getStatusClass(status) {
      const classes = {
        orcamento:
          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        vendido:
          'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        separado:
          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        saiu_entregar:
          'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
        entregue:
          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        cancelado: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      };
      return classes[status] || classes.orcamento;
    },
    getStatusLabel(status, quote = null) {
      if (status === 'saiu_entregar' && quote?.delivery_type === 'pickup') {
        return 'RETIROU';
      }
      const labels = {
        orcamento: this.t('QUOTES.STATUS.BUDGET'),
        vendido: this.t('QUOTES.STATUS.SOLD'),
        separado: 'SEPARANDO',
        saiu_entregar: 'SAIU P/ ENTREGAR',
        entregue: this.t('QUOTES.STATUS.DELIVERED'),
        cancelado: this.t('QUOTES.STATUS.CANCELLED'),
      };
      return labels[status] || status;
    },
    canEdit(quote) {
      return quote.status === 'orcamento';
    },
    canMarkAsSold(quote) {
      return quote.status === 'orcamento';
    },
    canSeparate(quote) {
      return quote.status === 'vendido';
    },
    canMarkAsOutForDelivery(quote) {
      return quote.status === 'separado';
    },
    canMarkAsDelivered(quote) {
      return quote.status === 'saiu_entregar';
    },
    canCancel(quote) {
      return ['orcamento', 'vendido', 'separado', 'saiu_entregar'].includes(
        quote.status
      );
    },
    canResendNotification(quote) {
      return (
        (quote.status === 'separado' || quote.status === 'saiu_entregar') &&
        quote.delivery_type === 'delivery' &&
        quote.delivery_person_name
      );
    },
  },
};
</script>

<template>
  <div class="quote-list">
    <!-- Empty State -->
    <div
      v-if="quotes.length === 0"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <div
        class="w-16 h-16 mb-4 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center"
      >
        <span class="i-lucide-file-text text-3xl text-slate-400" />
      </div>
      <h3 class="text-lg font-medium text-slate-700 dark:text-slate-200 mb-2">
        {{ t('QUOTES.EMPTY.TITLE') }}
      </h3>
      <p class="text-sm text-slate-500">
        {{ t('QUOTES.EMPTY.MESSAGE') }}
      </p>
    </div>

    <!-- Quote Cards -->
    <div v-else class="space-y-3">
      <div
        v-for="quote in quotes"
        :key="quote.id"
        class="quote-card bg-slate-50 dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 transition-colors"
      >
        <!-- Header -->
        <div class="flex items-start justify-between mb-3">
          <div>
            <div class="flex items-center gap-2">
              <span class="font-semibold text-slate-800 dark:text-slate-100">
                #{{ quote.id }}
              </span>
              <span
                :class="getStatusClass(quote.status)"
                class="px-2 py-0.5 text-xs font-medium rounded-full uppercase"
              >
                {{ getStatusLabel(quote.status, quote) }}
              </span>
              <span
                v-if="quote.quote_type === 'day_use'"
                class="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
              >
                🏊 Day-Use
              </span>
              <span
                v-if="quote.quote_type === 'hospedagem'"
                class="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
              >
                🏨 Hospedagem
              </span>
            </div>
            <p class="text-xs text-slate-500 mt-1">
              {{ formatDate(quote.created_at) }}
            </p>
          </div>
          <div class="text-right">
            <p class="text-lg font-bold text-slate-800 dark:text-slate-100">
              {{ formatCurrency(quote.total) }}
            </p>
            <p v-if="quote.discount > 0" class="text-xs text-green-600">
              {{ t('QUOTES.DISCOUNT') }}: {{ formatCurrency(quote.discount) }}
            </p>
          </div>
        </div>

        <!-- Items Count -->
        <div
          class="flex items-center gap-4 mb-3 text-sm text-slate-600 dark:text-slate-400 flex-wrap"
        >
          <span class="flex items-center gap-1">
            <span class="i-lucide-package text-base" />
            {{ quote.items_count || 0 }} {{ t('QUOTES.ITEMS') }}
          </span>
          <span v-if="quote.user_name" class="flex items-center gap-1">
            <span class="i-lucide-user text-base" />
            {{ quote.user_name }}
          </span>
          <span v-if="quote.sold_at" class="flex items-center gap-1">
            <span class="i-lucide-calendar-check text-base" />
            {{ t('QUOTES.SOLD_AT') }}: {{ formatDate(quote.sold_at) }}
          </span>
          <span v-if="quote.delivery_type" class="flex items-center gap-1">
            <span
              :class="
                quote.delivery_type === 'delivery'
                  ? 'i-lucide-truck'
                  : 'i-lucide-shopping-bag'
              "
              class="text-base"
            />
            {{ quote.delivery_type === 'delivery' ? 'Entrega' : 'Retirada' }}
            <template v-if="quote.delivery_person_name">
              - {{ quote.delivery_person_name }}
            </template>
          </span>
        </div>

        <!-- Delivery Address -->
        <div
          v-if="quote.delivery_address"
          class="mb-3 text-sm text-slate-600 dark:text-slate-400"
        >
          <span class="i-lucide-map-pin text-base mr-1" />
          {{ quote.delivery_address }}
        </div>

        <!-- Actions -->
        <div
          class="flex items-center gap-2 pt-3 border-t border-slate-200 dark:border-slate-700 flex-wrap"
        >
          <NextButton
            v-if="canEdit(quote)"
            sm
            faded
            slate
            :label="t('QUOTES.ACTIONS.EDIT')"
            icon="i-lucide-pencil"
            @click="$emit('edit', quote)"
          />
          <NextButton
            sm
            faded
            slate
            :label="t('QUOTES.ACTIONS.VIEW')"
            icon="i-lucide-eye"
            @click="$emit('view', quote)"
          />
          <NextButton
            v-if="canMarkAsSold(quote)"
            sm
            faded
            emerald
            :label="t('QUOTES.ACTIONS.MARK_SOLD')"
            icon="i-lucide-check-circle"
            @click="openPaymentModal(quote)"
          />
          <NextButton
            v-if="
              quote.status === 'vendido' &&
              (quote.quote_type === 'day_use' ||
                quote.quote_type === 'hospedagem')
            "
            sm
            faded
            green
            label="Enviar Voucher"
            icon="i-lucide-ticket"
            @click="$emit('send-voucher', quote)"
          />
          <NextButton
            v-if="quote.status === 'vendido'"
            sm
            faded
            amber
            label="Editar Venda"
            icon="i-lucide-pencil"
            @click="$emit('reopen-sale', quote)"
          />
          <NextButton
            v-if="quote.status === 'vendido'"
            sm
            faded
            blue
            label="Editar Pagamentos"
            icon="i-lucide-credit-card"
            @click="openPaymentModal(quote, true)"
          />
          <NextButton
            v-if="canSeparate(quote)"
            sm
            faded
            amber
            label="Separar Mercadoria"
            icon="i-lucide-package-check"
            @click="openSeparationModal(quote)"
          />
          <NextButton
            v-if="
              canMarkAsOutForDelivery(quote) &&
              quote.delivery_type === 'delivery'
            "
            sm
            faded
            orange
            label="Saiu p/ Entregar"
            icon="i-lucide-truck"
            @click="openOutForDeliveryModal(quote)"
          />
          <NextButton
            v-if="
              canMarkAsOutForDelivery(quote) &&
              quote.delivery_type !== 'delivery'
            "
            sm
            faded
            orange
            label="Marcar Retirado"
            icon="i-lucide-shopping-bag"
            @click="$emit('update-status', quote, 'saiu_entregar')"
          />
          <NextButton
            v-if="canMarkAsDelivered(quote)"
            sm
            faded
            blue
            :label="t('QUOTES.ACTIONS.MARK_DELIVERED')"
            icon="i-lucide-check-circle"
            @click="$emit('update-status', quote, 'entregue')"
          />
          <NextButton
            v-if="canResendNotification(quote)"
            sm
            faded
            amber
            label="Reenviar Msg"
            icon="i-lucide-send"
            title="Reenviar mensagem ao entregador"
            @click="$emit('resend-notification', quote)"
          />
          <NextButton
            v-if="canCancel(quote)"
            sm
            faded
            ruby
            :label="t('QUOTES.ACTIONS.CANCEL')"
            icon="i-lucide-x-circle"
            @click="$emit('update-status', quote, 'cancelado')"
          />
          <NextButton
            v-if="canEdit(quote)"
            sm
            faded
            ruby
            icon="i-lucide-trash-2"
            @click="$emit('delete', quote)"
          />
          <NextButton
            sm
            faded
            slate
            icon="i-lucide-printer"
            :title="t('QUOTES.ACTIONS.PRINT')"
            @click="$emit('print', quote)"
          />
        </div>
      </div>
    </div>

    <!-- Separation Type Modal -->
    <div
      v-if="showSeparationModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeSeparationModal"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-md shadow-xl"
      >
        <h3
          class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4"
        >
          Separar Mercadoria
        </h3>
        <p class="text-slate-600 dark:text-slate-400 mb-6">
          Como será a entrega do pedido #{{ selectedQuoteForSeparation?.id }}?
        </p>

        <div class="grid grid-cols-2 gap-4 mb-6">
          <button
            class="flex flex-col items-center justify-center p-6 rounded-lg border-2 border-slate-200 dark:border-slate-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            @click="selectDeliveryType('delivery')"
          >
            <span class="i-lucide-truck text-4xl text-blue-500 mb-2" />
            <span class="font-medium text-slate-800 dark:text-slate-200"
              >Entrega</span
            >
            <span class="text-xs text-slate-500">Separar para entrega</span>
          </button>

          <button
            class="flex flex-col items-center justify-center p-6 rounded-lg border-2 border-slate-200 dark:border-slate-600 hover:border-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            @click="selectDeliveryType('pickup')"
          >
            <span class="i-lucide-shopping-bag text-4xl text-green-500 mb-2" />
            <span class="font-medium text-slate-800 dark:text-slate-200"
              >Retirada</span
            >
            <span class="text-xs text-slate-500">Cliente retira</span>
          </button>
        </div>

        <div class="flex justify-end">
          <NextButton
            faded
            slate
            label="Cancelar"
            @click="closeSeparationModal"
          />
        </div>
      </div>
    </div>

    <!-- Out for Delivery - Driver Selection Modal -->
    <div
      v-if="showOutForDeliveryModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeOutForDeliveryModal"
    >
      <div
        class="bg-white dark:bg-slate-800 rounded-lg p-6 w-full max-w-md shadow-xl max-h-[80vh] overflow-hidden flex flex-col"
      >
        <h3
          class="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2"
        >
          Selecionar Entregador
        </h3>
        <p class="text-sm text-slate-500 dark:text-slate-400 mb-1">
          Pedido #{{ selectedQuoteForDelivery?.id }}
        </p>
        <p
          v-if="selectedQuoteForDelivery?.delivery_zone_name"
          class="text-sm font-medium text-blue-600 dark:text-blue-400 mb-4 flex items-center gap-1"
        >
          <span class="i-lucide-map-pin text-base" />
          Setor: {{ selectedQuoteForDelivery.delivery_zone_name }}
        </p>
        <p
          v-if="selectedQuoteForDelivery?.delivery_address"
          class="text-xs text-slate-500 dark:text-slate-400 mb-4"
        >
          {{ selectedQuoteForDelivery.delivery_address }}
        </p>

        <div v-if="loadingDeliveryPeople" class="flex justify-center py-8">
          <span
            class="i-lucide-loader-2 animate-spin text-2xl text-slate-400"
          />
        </div>

        <div v-else-if="deliveryPeople.length === 0" class="text-center py-8">
          <span class="i-lucide-user-x text-4xl text-slate-400 mb-2 block" />
          <p class="text-slate-600 dark:text-slate-400">
            Nenhum entregador cadastrado.
          </p>
          <p class="text-sm text-slate-500 mt-1">
            Cadastre entregadores nas configurações da conta.
          </p>
        </div>

        <div v-else class="flex-1 overflow-auto space-y-2">
          <button
            v-for="person in deliveryPeople"
            :key="person.id"
            class="w-full flex items-center gap-3 p-4 rounded-lg border border-slate-200 dark:border-slate-600 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors text-left"
            @click="selectDeliveryPerson(person)"
          >
            <div
              class="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center"
            >
              <span
                class="i-lucide-user text-xl text-blue-600 dark:text-blue-400"
              />
            </div>
            <div class="flex-1">
              <p class="font-medium text-slate-800 dark:text-slate-200">
                {{ person.name }}
              </p>
              <p class="text-sm text-slate-500">
                {{ person.phone_number }}
              </p>
            </div>
            <span class="i-lucide-chevron-right text-slate-400" />
          </button>
        </div>

        <div
          class="flex justify-end mt-4 pt-4 border-t border-slate-200 dark:border-slate-700"
        >
          <NextButton
            faded
            slate
            label="Cancelar"
            @click="closeOutForDeliveryModal"
          />
        </div>
      </div>
    </div>

    <!-- Payment Closing Modal -->
    <PaymentClosingModal
      v-if="selectedQuoteForPayment"
      :quote="selectedQuoteForPayment"
      :is-open="showPaymentModal"
      :edit-mode="isEditingPayment"
      @close="closePaymentModal"
      @confirm="confirmPayment"
    />
  </div>
</template>

<style scoped>
.quote-card {
  transition: all 0.2s ease;
}

.quote-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
}
</style>
